/**
 * What appears in the navigation, and who may see it.
 *
 * WHY THE PERMISSION STRINGS LOOK LIKE THAT
 * -----------------------------------------
 * They are the ones the backend actually issues, which follow the shape
 * `module.action.module` — `pos.read.pos`, not `pos.read.order`. An invented
 * name does not error anywhere: it simply never matches, so the entry silently
 * disappears for everyone except the owner, who bypasses the check entirely.
 * That is exactly what happened when this list was first written, which is why
 * NAV_PERMISSIONS is asserted against a real cashier's set in the tests.
 *
 * Hiding an entry is a courtesy, not a control. The backend checks the same
 * permission again on every request.
 */
import {
  TillIcon,
  KitchenIcon,
  FloorIcon,
  OrdersIcon,
  MenuIcon,
  StockIcon,
  PurchasingIcon,
  ReportsIcon,
  DeliveryIcon,
  ExpensesIcon,
  BankingIcon,
  VouchersIcon,
  BranchesIcon,
  StaffIcon,
  CustomersIcon,
  SettingsIcon,
  FoodpandaIcon,
  ApprovalsIcon,
} from './icons.jsx';

export const NAV = Object.freeze([
  { to: '/pos', label: 'Till', permission: 'pos.create.pos', Icon: TillIcon },
  { to: '/floor', label: 'Floor', permission: 'pos.read.pos', Icon: FloorIcon },
  { to: '/kitchen', label: 'Kitchen', permission: 'kitchen.read.kitchen', Icon: KitchenIcon },
  { to: '/orders', label: 'Orders', permission: 'pos.read.pos', Icon: OrdersIcon },
  {
    to: '/delivery',
    label: 'Delivery',
    permission: 'delivery.read.delivery',
    Icon: DeliveryIcon,
  },
  { to: '/menu', label: 'Menu', permission: 'menu.read.menu', Icon: MenuIcon },
  /** The two shelves, buying, issuing and audits — the store keeper's whole day. */
  { to: '/store', label: 'Store', permission: 'inventory.read.inventory', Icon: StockIcon },
  {
    to: '/purchasing',
    label: 'Purchasing',
    permission: 'purchasing.read.purchasing',
    Icon: PurchasingIcon,
  },
  { to: '/expenses', label: 'Expenses', permission: 'expense.read.expense', Icon: ExpensesIcon },
  { to: '/reports', label: 'Reports', permission: 'reports.read.reports', Icon: ReportsIcon },
  /**
   * Where a manager clears the cashiers' requests — a cancel or an edit sent up
   * for approval. Gated on the same permission the approval itself needs, so
   * only somebody who could approve one ever sees the queue. A cashier, who
   * cannot, never does.
   */
  { to: '/approvals', label: 'Approvals', permission: 'pos.approve.pos', Icon: ApprovalsIcon },
  /**
   * What Foodpanda still owes. Read by whoever answers for the takings — the
   * same people the reports and the staff-account tab are for — so it is gated
   * on the reports permission, which a cashier does not hold. The backend
   * restricts clearing to the owner and managers again regardless.
   */
  { to: '/foodpanda', label: 'Foodpanda', permission: 'reports.read.reports', Icon: FoodpandaIcon },
  {
    to: '/banking',
    label: 'Banking',
    permission: 'accounting.read.accounting',
    Icon: BankingIcon,
  },
  {
    to: '/vouchers',
    label: 'Vouchers',
    permission: 'accounting.read.accounting',
    Icon: VouchersIcon,
  },
  /**
   * Keying in sales the shop took before it had the software, dated to the day
   * they happened. Gated on the same authority as a manual voucher — posting to
   * the books — so a cashier never sees it and cannot back-date a real sale.
   */
  {
    to: '/past-orders',
    label: 'Past orders',
    permission: 'accounting.create.accounting',
    Icon: OrdersIcon,
  },
  {
    to: '/branches',
    label: 'Branches',
    permission: 'branch.read.branch',
    Icon: BranchesIcon,
  },
  { to: '/customers', label: 'Customers', permission: 'crm.read.crm', Icon: CustomersIcon },
  { to: '/staff', label: 'Staff', permission: 'auth.read.auth', Icon: StaffIcon },
  /**
   * Last, and only for whoever may change them. These are the rules the shop
   * runs on rather than the work of a shift, so they belong at the bottom of
   * the list somebody reads top to bottom all day.
   */
  { to: '/settings', label: 'Settings', permission: 'settings.read.settings', Icon: SettingsIcon },
]);

/** Exported so tests can check these against what the backend really issues. */
export const NAV_PERMISSIONS = NAV.map((item) => item.permission).filter(Boolean);

/**
 * Where to send somebody who has just signed in.
 *
 * WHY THIS IS NOT SIMPLY THE TILL
 * -------------------------------
 * It used to be. Every sign-in landed on `/pos`, whatever the account was —
 * so the kitchen screen signed in and was told "You do not have access to
 * this", which is a true statement and a terrible first impression. The
 * kitchen has no business on the till and never will; it was sent somewhere it
 * is not allowed to be and then blamed for being there.
 *
 * The navigation is already an ordered list of everywhere a person can go,
 * most-used first, with the permission each one needs. The first entry they
 * hold the permission for is, by construction, the right place to open on: a
 * cashier gets the till, the kitchen gets the kitchen screen, a rider gets the
 * dispatch board.
 *
 * WHY SOME JOBS ARE NAMED AND MOST ARE NOT
 * ----------------------------------------
 * The list is ordered for a person browsing it, not for opening on. Kitchen
 * staff can see the floor plan, and Floor sits above Kitchen — so scanning
 * alone put the wall screen in the kitchen on the seating chart, which is
 * accessible, useless, and not what anybody wants at six in the evening.
 *
 * Where a job has an obvious home it is named. Everyone else takes the first
 * screen they can reach, which is the till for anybody who sells and something
 * sensible for anybody who does not.
 *
 * Returns null when somebody can reach nothing at all, which is a real state —
 * a new account with no role yet — and wants saying rather than redirecting in
 * a circle.
 *
 * @param {(permission: string) => boolean} can
 * @param {string[]} [roles] the roles this person holds
 */
const HOME_BY_ROLE = Object.freeze({
  kitchen_staff: '/kitchen',
  rider: '/delivery',
  dispatcher: '/delivery',

  /**
   * A waiter has no cash drawer.
   *
   * Without an entry here they fell through to the till, which opens by
   * asking whoever is in front of it to count the money in the drawer
   * before anything else can happen. A waiter carries a phone and a
   * notepad; the question is meaningless and there is nothing to answer
   * it with.
   *
   * The floor plan is what they actually work from.
   */
  waiter: '/floor',

  /**
   * Whoever runs the place opens on the figures, not on the till.
   *
   * The till is a frame with no navigation in it — deliberately, because a
   * cashier stands at that one screen for hours and a row of targets beside
   * the Charge button is where a mis-tap costs most. That is right for the
   * person selling and wrong for everybody else: an owner signing in to look
   * at their day landed on a sales screen with no way through to anything,
   * and had to know there was a quiet "Back office" link to find.
   *
   * Reports is what they came for.
   */
  super_admin: '/reports',
  owner: '/reports',
  branch_manager: '/reports',

  /** The store keeper's day is the two shelves. */
  store_keeper: '/store',
  inventory_manager: '/store',
});

export function homeFor(can, roles = []) {
  for (const role of roles) {
    const named = HOME_BY_ROLE[role];
    // Still checked, so a role stripped of its permission does not land on a
    // screen that will refuse it.
    if (named) {
      const entry = NAV.find((item) => item.to === named);
      if (!entry?.permission || can(entry.permission)) return named;
    }
  }

  const first = NAV.find((item) => !item.permission || can(item.permission));
  return first?.to ?? null;
}

/**
 * Somewhere other than the till, for whoever is standing at it.
 *
 * The till's own frame offered a way out only to people who could read
 * reports — which a cashier cannot. So a cashier landed on the till and had
 * no route to the floor plan, the kitchen screen or the order list, all of
 * which they are allowed to see and none of which they could reach without
 * signing out.
 *
 * Returns the first screen they can get to that is not the till, which is
 * also a screen with the navigation on it — so one press is enough to reach
 * everything else they hold.
 *
 * @param {(permission: string) => boolean} can
 */
export function elsewhereFor(can) {
  /**
   * The back office first, for anybody who has one.
   *
   * Somebody who can read reports and finds themselves on the till is a
   * manager or an owner, and what they want is the day's figures — not the
   * floor plan, which merely happens to sit higher in the list. Everyone else
   * gets the first screen they can open, whatever it is.
   */
  const back = NAV.find((item) => item.to === '/reports');
  if (back && (!back.permission || can(back.permission))) return back;

  const first = NAV.find(
    (item) => item.to !== '/pos' && (!item.permission || can(item.permission))
  );

  return first ?? null;
}
